import React, { useState, useEffect, useCallback } from "react";
import { Link, Navigate, useLocation } from "react-router-dom";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import RouteOutlinedIcon from '@mui/icons-material/RouteOutlined';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import TodayOutlinedIcon from '@mui/icons-material/TodayOutlined';
import ManageAccountsOutlinedIcon from '@mui/icons-material/ManageAccountsOutlined';
import LocalGasStationOutlinedIcon from '@mui/icons-material/LocalGasStationOutlined';
import TimelineDiagram from "../../../components/timelineDiagram/timelineDiagram";
import TimelineChart from "../../../components/timelineChart/timelineChart";
import TimelineMap from "../../../components/timelineMap/timelineMap";
import axiosInstance from "../../../api/axiosInstance";
import "../timeline/timeline.scss";

const Timeline = () => {
    const today = new Date().toISOString().split("T")[0];

    const [data, setData] = useState({});
    const [isLoading, setLoading] = useState(true);
    const [selectedDate, setSelectedDate] = useState(today);

    const location = useLocation();
    const slug = location.pathname.split("/")[3];

    const fetchData = useCallback(async () => {
        try {
            const res = await axiosInstance.get(
                `map/${slug}?date=${selectedDate}`
            );
            setData(res.data);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, [slug, selectedDate]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const handleDateChange = (e) => {
        setSelectedDate(e.target.value);
    };

    const isToday = selectedDate === today;
    const addDay = () => {
        if (isToday) return;
        const d = new Date(selectedDate);
        d.setDate(d.getDate() + 1);
        setSelectedDate(d.toISOString().split("T")[0]);
    };

    const subtractDay = () => {
        const d = new Date(selectedDate);
        d.setDate(d.getDate() - 1);
        setSelectedDate(d.toISOString().split("T")[0]);
    };

    const handleTodayClick = () => {
        setSelectedDate(today);
    };

    if (isLoading) return <div className="loading" />;

    if (data.imei == null) return <Navigate to="/unauthorized" replace />;

    return (
        <div className="mapTimeline">
            <div className="top">
                <div className="left">
                    <Link to="/map" className="back">
                        <ArrowBackIcon />
                    </Link>
                    <span className="heading">Zeitachse</span>
                    <span className="description">
                        {data.itemAlias} – {data.itemDescription}
                    </span>
                </div>
                <div className="right">
                    <button onClick={handleTodayClick} className="light today">Heute</button>
                    <input
                        type="date"
                        value={selectedDate}
                        max={today}
                        onChange={handleDateChange}
                        className="date"
                    />
                    <button onClick={subtractDay} className="light arrow">
                        <KeyboardArrowLeftIcon />
                    </button>
                    <button onClick={addDay} className="light arrow">
                        <KeyboardArrowRightIcon />
                    </button>
                </div>
            </div>
            <div className="content">
                <div className="mobileTop">
                    <div className="left">
                        <span className="description">
                            {data.itemAlias} – {data.itemDescription}
                        </span>
                    </div>
                    <div className="right">
                        <button onClick={handleTodayClick} className="light today">
                            <TodayOutlinedIcon />
                        </button>
                        <button onClick={subtractDay} className="light arrow">
                            <KeyboardArrowLeftIcon />
                        </button>
                        <button onClick={addDay} className="light arrow">
                            <KeyboardArrowRightIcon />
                        </button>
                    </div>
                </div>
                <div className="left">
                    <TimelineMap timelinePoints={data.timelinePoints} />
                    <div className="ignition">
                        <h3>Zündung</h3>
                        <TimelineDiagram
                            data={data.ignitionHistory}
                            date={selectedDate}
                        />
                    </div>
                </div>
                <div className="right">
                    {data.itemDriverAlias && (
                        <div className="data">
                            <div className="group" title="Fahrer">
                                <ManageAccountsOutlinedIcon />
                                <span>{data.itemDriverAlias}</span>
                            </div>
                        </div>
                    )}
                    {data.fuelCurve && (
                        <div className="data">
                            <div className="group chart" title="Tankinhalt">
                                <LocalGasStationOutlinedIcon />
                                <TimelineChart data={data.fuelCurve} unit={data.fuelLevel ? "%" : "l"} />
                            </div>
                        </div>
                    )}
                    {data.mileageHistory && (
                        <div className="data">
                            <div className="group" title="Tachostand Tagesende">
                                <RouteOutlinedIcon />
                                <span>{data.mileageHistory.toLocaleString("de-DE")} km</span>
                            </div>
                        </div>
                    )}
                    <div className="rights">
                        <h3>Rechte</h3>
                        <div className="list">
                            <div className="group">
                                <GroupOutlinedIcon />
                                <span>admin</span>
                            </div>
                            {data.permissions[0].imei && data.permissions.map(item => (
                                <div className="group" key={item.id}>
                                    <GroupOutlinedIcon />
                                    <span>{item.role}</span>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Timeline;